import { I18nPluralPipe } from '@angular/common';
import { Component, computed, effect, inject, input, linkedSignal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucidePlus } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmIconImports } from '@spartan-ng/helm/icon';

import { Région } from '../administration/gestion-sites/model/région.model';
import { Site } from '../administration/gestion-sites/model/site.model';
import { RégionStore } from '../administration/gestion-sites/store/région.store';
import { CartographieStore } from '../cartographie/store/cartographie.store';
import { Étape } from '../référentiel-métier-parcours/models/étape.model';
import { ChipComponent } from '../shared/components/chip/chip.component';
import { EmptyStateComponent } from '../shared/components/empty-state/empty-state.component';
import { ExpansionPanel } from '../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../shared/components/sidepanel/sidepanel.service';
import { TrierParPipe } from '../shared/pipes/trier-par.pipe';
import { EtapeItemComponent } from './components/etape-item/etape-item.component';
import { FiltresComponent } from './components/filtres/filtres.component';
import { HeaderComponent } from './components/header/header.component';
import { ÉTAT_GROUPE } from './enum/état-groupe.enum';
import { GetEtatEtapePipe } from './pipes/get-etat-etape.pipe';
import { ReferentielStore } from '../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-offre-regionale',
  imports: [
    HeaderComponent,
    TrierParPipe,
    HlmCardImports,
    ExpansionPanel,
    EmptyStateComponent,
    HlmIconImports,
    HlmButtonImports,
    RouterModule,
    EtapeItemComponent,
    ChipComponent,
    I18nPluralPipe,
  ],
  templateUrl: './offre-regionale.component.html',
  providers: [provideIcons({ lucidePlus }), GetEtatEtapePipe],
})
export class OffreRegionaleComponent {
  readonly #régionStore = inject(RégionStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #référentielStore = inject(ReferentielStore);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #getEtatEtapePipe = inject(GetEtatEtapePipe);

  readonly cartographie = computed(() => this.#cartographieStore.selectedEntity());
  readonly idSite = input<string>(undefined, { alias: 'site' });
  readonly idRegion = input<string>(undefined, { alias: 'region' });
  readonly idMetier = input<string>(undefined, { alias: 'metier' });

  readonly peutModifier = computed(() => !this.cartographie()?.estVerrouilleeOffreRegionale);

  readonly filtres = computed(() => ({ region: this.idRegion(), site: this.idSite(), metier: this.idMetier() }));

  readonly regions = linkedSignal(() => this.#régionStore.entities());

  readonly etatGroupe = ÉTAT_GROUPE;

  protected readonly formationMapping: { [k: string]: string } = {
    '=0': '# formation',
    '=1': '# formation',
    other: '# formations',
  };

  constructor() {
    effect(() => {
      const cartographie = this.cartographie();
      if (cartographie) {
        this.#référentielStore.chargerParCartographie(cartographie.id);
        this.#régionStore.charger(cartographie.id);
      }
    });

    effect(() => {
      const idRegion = this.idRegion();
      const idSite = this.idSite();
      const idMetier = this.idMetier();

      if (!(idRegion || idSite || idMetier)) {
        this.regions.set(this.#régionStore.entities());
        return;
      }

      const matchMetier = (metier: string) => (idMetier ? metier === idMetier : true);
      const matchSite = (site: Site) => (idSite ? site.id === idSite : true);
      const matchRegion = (region: string) => (idRegion ? region === idRegion : true);

      this.regions.set(
        this.#régionStore
          .entities()
          .map((region) => {
            const filteredSite = region.sites
              .map((site) => {
                const filteredMetier = site.etapes.filter((x) => matchMetier(x.idReferentiel));

                if (!filteredMetier.length && idMetier) return null;

                return {
                  ...site,
                  etapes: filteredMetier,
                };
              })
              .filter((x) => x !== null)
              .filter((x) => matchSite(x));

            if (!filteredSite.length && (idSite || idMetier)) return null;

            return {
              ...region,
              sites: filteredSite.length > 0 ? filteredSite : region.sites,
            };
          })
          .filter((x) => x !== null)
          .filter((x) => matchRegion(x.id)),
      );
    });
  }

  openFilterPanel() {
    this.#sidepanelService.setComponentToDisplay(
      'Filtrer',
      FiltresComponent,
      'Vous pouvez filtrer les éléments qui seront affichés dans l’offre régionale pour faciliter votre recherche.',
      {
        filtres: this.filtres(),
        cartographie: this.cartographie(),
      },
    );
    this.#sidepanelService.open();
  }

  getEtapeNumberInRegion = (région: Région) => région.sites.flatMap((x) => x.etapes).length;
  getEtapeNumberParEtatInSite = (étapes: Étape[], état: ÉTAT_GROUPE) =>
    `${étapes.filter((x) => this.#getEtatEtapePipe.transform(x.groupes) === état).length}/${étapes.length}`;
  getEtapeNumberParEtatInRegion = (sites: Site[], état: ÉTAT_GROUPE) =>
    `${sites.flatMap((x) => x.etapes).filter((x) => this.#getEtatEtapePipe.transform(x.groupes) === état).length}/${sites.flatMap((x) => x.etapes).length}`;
}

export interface FiltresOffreRegionale {
  region?: string;
  site?: string;
  metier?: string;
}
